import { ref, onMounted, onUnmounted, nextTick } from "vue";

// Constants matching recipe label system
const PX_PER_INCH = 96;
const SNAP_TO_INCREMENTS = [0.1, 0.125, 1 / 3];

/**
 * Snap a value to the closest increment from SNAP_TO_INCREMENTS.
 */
function snapToIncrement(value) {
  let best = value;
  let bestDiff = Infinity;

  for (const inc of SNAP_TO_INCREMENTS) {
    const candidate = Math.round(value / inc) * inc;
    const diff = Math.abs(candidate - value);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = candidate;
    }
  }
  return Number(best.toFixed(3));
}

/**
 * Format a number, removing trailing zeros and decimal point if not needed.
 */
function formatDecimal(val) {
  let s = val.toFixed(3);
  s = s.replace(/(\.\d*?)0+$/, "$1");
  s = s.replace(/\.$/, "");
  return s;
}

/**
 * Convert pixels to inches and snap to increments.
 */
function pxToInches(px) {
  return snapToIncrement(px / PX_PER_INCH);
}

/**
 * Format dimensions as "width × height" string.
 */
export function formatDimensions(widthPx, heightPx) {
  const w = pxToInches(widthPx);
  const h = pxToInches(heightPx);
  return `${formatDecimal(w)} × ${formatDecimal(h)}`;
}

/**
 * Composable for measuring element dimensions with ResizeObserver.
 *
 * @param {Function} getElement - Function that returns the element to observe
 * @returns {Object} - { dimensionsText, widthPx, heightPx }
 */
export function useDimensions(getElement) {
  const widthPx = ref(0);
  const heightPx = ref(0);
  const dimensionsText = ref("");

  let resizeObserver = null;

  function updateDimensions(element) {
    if (!element) {
      dimensionsText.value = "";
      return;
    }

    const rect = element.getBoundingClientRect();
    widthPx.value = rect.width;
    heightPx.value = rect.height;

    if (rect.width > 0 && rect.height > 0) {
      dimensionsText.value = formatDimensions(rect.width, rect.height);
    } else {
      dimensionsText.value = "";
    }
  }

  function startObserving() {
    const element = getElement();
    if (!element) return;

    // Initial measurement
    updateDimensions(element);

    // Watch for size changes
    if ("ResizeObserver" in window) {
      resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          updateDimensions(entry.target);
        }
      });
      resizeObserver.observe(element);
    }
  }

  function stopObserving() {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  }

  onMounted(() => {
    // Delay slightly to ensure DOM is fully rendered
    nextTick(() => {
      requestAnimationFrame(() => {
        startObserving();
      });
    });
  });

  onUnmounted(() => {
    stopObserving();
  });

  return {
    dimensionsText,
    widthPx,
    heightPx,
    // Expose for manual refresh if needed
    refresh: () => updateDimensions(getElement()),
  };
}
